import { ImportDataLegalEntitiesQuery } from '@/__generated__/graphql';
import { IEntityCodesTableData, MissingIdError } from '@/components/settings-page/importData/types';
import FilterPage from '@/components/table/filterPage';
import { TableBodyWrapper, TableWrapper } from '@/components/table/styles';
import TableHeader from '@/components/table/tableHeader';
import { ISort } from '@/components/table/types';
import { useResponsive } from '@/hooks/use-responsive';
import { useEffect, useState } from 'react';
import { columns } from '../constants';
import MobileTable from './mobileTable';
import TableBody from './tableBody';

interface TableProps {
  tableData: IEntityCodesTableData[];
  errorEntityCode: MissingIdError[];
  sortedLegalEntities: ImportDataLegalEntitiesQuery['LegalEntities'];
  legalEntitiesLoading: boolean;
  handleChangeAssignment: (entityId: string, prevValue: string, entityCode: string) => void;
  resetAssignment: (entityCode: string, prevValue: string) => void;
}

const Table = ({ tableData, errorEntityCode, sortedLegalEntities, legalEntitiesLoading, handleChangeAssignment, resetAssignment }: TableProps) => {
  const { isMobile } = useResponsive();
  const [sort, setSort] = useState<ISort>({ key: 'ENTITY_CODE', asc: true });
  const [search, setSearch] = useState('');
  const [primaryFilter, setPrimaryFilter] = useState('All Entity Codes');
  const [sortedTableData, setSortedTableData] = useState<IEntityCodesTableData[]>([]);

  useEffect(() => {
    if (errorEntityCode?.length) {
      setPrimaryFilter('Unrecognized Entity Codes');
      return;
    }
    setPrimaryFilter('All Entity Codes');
  }, []);

  useEffect(() => {
    const sortedData = [...tableData].sort((a, b) => {
      switch (sort.key) {
        case 'ENTITY_CODE':
          return sort.asc ? a.entityCode.localeCompare(b.entityCode) : b.entityCode.localeCompare(a.entityCode);
        case 'ENTITY_NAME':
          return sort.asc ? a.entityName.localeCompare(b.entityName) : b.entityName.localeCompare(a.entityName);
        case 'ASSIGNMENT':
          const aAssignment = a.entityAssignment === 'Select An Entity' ? '' : a.entityAssignment;
          const bAssignment = b.entityAssignment === 'Select An Entity' ? '' : b.entityAssignment;
          return sort.asc ? aAssignment.localeCompare(bAssignment) : bAssignment.localeCompare(aAssignment);
        case 'STATUS':
          return sort.asc ? a.entityStatus.localeCompare(b.entityStatus) : b.entityStatus.localeCompare(a.entityStatus);
        default:
          return 0;
      }
    });

    setSortedTableData(sortedData);
  }, [sort, tableData]);

  const filteredTableData = () => {
    const newTableData = [...sortedTableData];
    const filteredTableData = newTableData.filter(
      (item) => item.entityName?.toLowerCase().includes(search.toLowerCase()) || item.entityCode.toLowerCase().includes(search.toLowerCase())
    );
    if (primaryFilter !== 'Unrecognized Entity Codes') return filteredTableData;

    return filteredTableData.filter((item) => item.entityStatus === 'Required' || item.entityStatus === 'Complete');
  };

  return (
    <>
      <FilterPage
        search={{
          value: search,
          onChange: setSearch,
          placeholder: 'Search'
        }}
        primaryFilter={{
          value: primaryFilter,
          onChange: setPrimaryFilter,
          selects: ['All Entity Codes', 'Unrecognized Entity Codes']
        }}
        resultsValue={filteredTableData().length}
        refetch={() => null as any}
      />
      <TableWrapper padding="0">
        {isMobile ? (
          <MobileTable
            tableData={filteredTableData()}
            sortedLegalEntities={sortedLegalEntities}
            legalEntitiesLoading={legalEntitiesLoading}
            handleChangeAssignment={handleChangeAssignment}
            resetAssignment={resetAssignment}
          />
        ) : (
          <>
            <TableHeader refetch={() => null as any} columns={columns} savedSort={sort} savedSetSort={setSort} />
            <TableBodyWrapper>
              <TableBody
                tableData={filteredTableData()}
                sortedLegalEntities={sortedLegalEntities}
                legalEntitiesLoading={legalEntitiesLoading}
                handleChangeAssignment={handleChangeAssignment}
                resetAssignment={resetAssignment}
              />
            </TableBodyWrapper>
          </>
        )}
      </TableWrapper>
    </>
  );
};

export default Table;
